import { formatDate } from "../helpers/DateFormatter";
import { ILogData, IRunData, IRunLog, LogFilters, LogsResponse, RunsResponse } from "../interfaces/ILogs";
import { TaskName } from "../mock/MockData";
import axiosInstance from "../utils/axiosConfig";

export async function getAllLogs(filters: LogFilters = {}): Promise<RunsResponse> {
  try {
    const {
      date,
      tasks = [],
      files = [],
      status = [],
      pageNumber = 1,
      pageSize = 10,
      sortField,
      sortDirection,
    } = filters;

    // Construct query params
    const params = new URLSearchParams();

    if (date) params.append("fromDate", date);

    tasks.forEach(task => params.append("Task", task));
    files.forEach(file => params.append("Files", file));
    status.forEach(status => params.append("Status", status));

    params.append("pageNumber", pageNumber.toString());
    params.append("pageSize", pageSize.toString());

    if (sortField) params.append("sortField", sortField);
    if (sortDirection) params.append("sortDirection", sortDirection);

    const url = `logs/runs?${params.toString()}`;
    const response = await axiosInstance.get(url);

    if (!response?.data || !response.data.RunProgress) {
      return { data: [], status: response?.status || 500, message: "No data found", totalPages: 1 };
    }

    const logs: IRunData[] = response.data.RunProgress.map((run: IRunData) => ({
      RunId: run.RunId,
      BlobUrl: run.BlobUrl,
      BlobName: run.BlobName,
      TaskName: run.TaskName,
      MessageTypeCode: run.MessageTypeCode,
      LatestTimestamp: new Date(run.LatestTimestamp).toISOString(),
      LatestStatus: run.LatestStatus,
      Description: run.Description,
      LatestStep: run.LatestStep,
      Logs: run.Logs.map((log: IRunLog) => ({
        MessageTypeCode: log.MessageTypeCode,
        Timestamp: new Date(log.Timestamp).toISOString(),
        Status: log.Status,
        Description: log.Description,
        Step: log.Step,
      })),
    }));

    return {
      data: logs,
      status: response.status,
      message: "Logs fetched successfully",
      totalPages: response.data.TotalPages || 1,
    };
  } catch (error: any) {
    console.error("Error fetching logs:", error.response?.data || error.message);
    return {
      data: [],
      status: error.response?.status || 500,
      message: error.response?.data || error.message || "An unexpected error occurred",
      totalPages: 1
    };
  }
}


export async function getLogsByRunId(
  runId: string
): Promise<LogsResponse> {
  try {

    // Construct the query parameters dynamically
    const url = `logs/runs/${runId}`;

    // Perform API request
    const response = await axiosInstance.get(url);

    if (!response?.data ||
      (response.status == 200 && response.data.length == 0)) {
      return { data: [], status: response.status, message: "No logs found" };
    }

    // Map response to match ILogData
    const logs: ILogData[] = response.data.map((log: ILogData) => {
      return {
        Id: log.Id,
        RunId: log.RunId,
        BlobUrl: log.BlobUrl,
        BlobName: log.BlobName,
        TaskName: log.TaskName,
        Status: log.Status,
        Step: log.Step,
        Description: log.Description,
        Timestamp: formatDate(log.Timestamp),
        DateCreated: formatDate(log.DateCreated),
        DateUpdated: formatDate(log.DateUpdated),
      };
    });

    return { data: logs, status: response.status, message: "Logs fetched successfully" };

  } catch (error: any) {
    console.error("Error fetching logs:", error.response?.data || error.message);
    return {
      data: [],
      status: error.response?.status || 500,
      message: error.response?.data?.message || "An unexpected error occurred",
    };
  }
}

export async function setLogDeleteFrequency(id: string, frequency: number): Promise<any> {
  try {
    const url = `logs/config/${id}`;
    const response = await axiosInstance.put(url, { value: frequency });

    return response;
  } catch (error) {
    console.error("Error setting log delete frequency:", error);
    throw error;
  }
}

export async function getLogDeleteFrequency(id: string): Promise<any> {
  try {
    const url = `logs/config/${id}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching log delete frequency:", error);
    throw error;
  }
}

export async function getBlobData(blobUrl: string): Promise<Blob> {
  try {
    const url = `/logs/blob/${blobUrl}`;
    const response = await axiosInstance.get(url, { responseType: "blob" });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch blob: ${response.statusText}`);
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching blob data:", error);
    throw error;
  }
}